import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Spin, Button } from 'antd';
import V2Container from '@/common/components/Data/V2Container';
import V2Drawer from '@/common/components/Feedback/V2Drawer';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Operate from '@/common/components/Others/V2Operate';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import FranchiseeDetail from '@/common/components/business/ExpandStore/FranchiseeDetail';
import styles from './index.module.less';
// import cs from 'classnames';
import { refactorPermissions } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import SkeletonDetail from '@/common/components/Skeleton/SkeletonDetail';
import { useBigdataFn } from '@/common/utils/bigdata';
import UserInfoContext from '@/layout/context';
import { globalAlias, globalExpansionTaskAlias, globalFranchiseeAlias } from '@/common/utils/ways';

import { useSelector } from 'react-redux';
import { userPermissionList } from '@/common/api/permission';
import SelectTemplateModal from '../ChanceCreateDrawer/SelectTemplateModal';
import ChanceCreateDrawer from '../ChanceCreateDrawer';
import { getFranchiseeAddress } from '../ChanceCreateDrawer/franchiseePrefill';

/**
 * @Description 加盟商详情抽屉
 */
const DetailDrawer: FC<{
  /** 抽屉数据，fromPublicSea 为 true 表示从公海列表打开，顶部操作按钮隐藏（只读查看） */
  detailData: any;
  setDetailData: (data: any) => void;
  onSearch: any;
}> = ({
  detailData,
  setDetailData,
  onSearch,
}) => {
  const bigdataFn = useBigdataFn(); // 使用埋点 Hook
  const detailRef = useRef<any>(null);
  const [mainHeight, setMainHeight] = useState<number>(0);
  const [detail, setDetail] = useState<any>({}); // 详情信息
  const [loading, setLoading] = useState<boolean>(false); // 加载中
  const [submitting, setSubmitting] = useState<boolean>(false); // 提交中
  const [isUpdateMode, setIsUpdateMode] = useState<boolean>(false); // 是否开启编辑模式
  const [loadingSk, setLoadingSk] = useState<any>(true); // 骨架屏
  const { id: userId, tenantInfo: { globalAliasItemVOs, id: tenantId } } = useContext(UserInfoContext); // 租户信息
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const chancepointAlias = globalAlias(globalAliasItemVOs);
  const isYiMing = !!useSelector((state: any) => state.common.tenantCheck?.isYiMing);
  const sourceScope = useMemo(() => ({}), [detailData.open, detailData.id, detailData.fromPublicSea, tenantId, userId, isYiMing]);
  const sourceScopeRef = useRef<any>(sourceScope);
  sourceScopeRef.current = sourceScope;
  const detailRequestRef = useRef(0);
  const [loadedScope, setLoadedScope] = useState<any>();
  const [createPermission, setCreatePermission] = useState<any>();
  const [templateSession, setTemplateSession] = useState<any>();
  const templateSessionRef = useRef<any>();
  const [chanceDrawerData, setChanceDrawerData] = useState<any>();
  const canCreateChance = !!(detailData.open && !detailData.fromPublicSea && !isUpdateMode && isYiMing &&
    loadedScope === sourceScope && createPermission?.scope === sourceScope && createPermission.allowed &&
    getFranchiseeAddress(detail));

  const expansionTaskAlias = globalExpansionTaskAlias(globalAliasItemVOs);

  const methods = useMethods({
    getDetail() {
      const scope = sourceScope;
      const request = ++detailRequestRef.current;
      setLoading(true);
      setLoadedScope(undefined);

      const { id } = detailData;
      getFranchiseeDetail({ id }).then((res) => {
        if (sourceScopeRef.current !== scope || request !== detailRequestRef.current) return;
        res && setDetail(res);
        res && setLoadedScope(scope);
        // res && setLoadingSk(false);
      }).catch(() => {}).finally(() => {
        if (sourceScopeRef.current !== scope || request !== detailRequestRef.current) return;
        setLoading(false);
        setLoadingSk(false);
      });
    },
    loadCreatePermission() {
      const scope = sourceScope;
      userPermissionList({ encodeList: ['standardChancePoint:create'] }).then((res) => {
        if (sourceScopeRef.current !== scope) return;
        setCreatePermission({ scope, allowed: Array.isArray(res) && res.some(item => item?.enCode === 'standardChancePoint:create') });
      }).catch(() => {
        if (sourceScopeRef.current === scope) setCreatePermission({ scope, allowed: false });
      });
    },
    handleCreateChance() {
      if (!canCreateChance) return;
      const session = { scope: sourceScope };
      templateSessionRef.current = session;
      setTemplateSession(session);
    },
    setTemplateVisible(visible, session) {
      if (session !== templateSessionRef.current || session.scope !== sourceScopeRef.current) return;
      if (!visible) {
        templateSessionRef.current = undefined;
        setTemplateSession(undefined);
      }
    },
    selectChanceTemplate({ templateId }, session) {
      if (!canCreateChance || !templateId || session !== templateSessionRef.current || session.scope !== sourceScopeRef.current) return;
      setChanceDrawerData({ open: true, templateId, id: '', source: detail, scope: sourceScope });
    },
    closeChanceDrawer() {
      setChanceDrawerData(undefined);
    },
    /**
     * @description 点击编辑按钮，开启编辑模式
     */
    handleUpdate() {
      setIsUpdateMode(true);
      bigdataFn('1be30bf7-cb37-4957-921c-af457b9e1696', '加盟商详情-编辑', '点击了加盟商详情-编辑', { op_block: '加盟商管理', op_event: ``, });
    },
    /**
     * @description 提交编辑信息
     */
    confirmHandle() {
      detailRef.current?.confirmHandle();
    },
    /**
     * @description 取消编辑
     */
    cancelHandle() {
      setIsUpdateMode(false);
    },

    // 内部回调出来的刷新事件
    refresh() {
      methods.getDetail(); // 详情接口刷新
      onSearch && onSearch(); // 外部刷新回调
    },

    onCloseDrawer() {
      sourceScopeRef.current = undefined;
      templateSessionRef.current = undefined;
      setTemplateSession(undefined);
      setChanceDrawerData(undefined);
      setCreatePermission(undefined);
      setLoadedScope(undefined);
      setDetailData({
        visible: false
      });
      isUpdateMode && setIsUpdateMode(false);
      setLoadingSk(true);
      bigdataFn('05376169-37bb-47ad-89b2-c79999419f52', '加盟商详情-关闭', '点击了加盟商详情-关闭', { op_block: '加盟商管理', op_event: ``, });
    },

    /** 移交 */
    handleTransfer() {
      detailRef.current?.transfer();
    },

    /** 共享 */
    handleShare() {
      detailRef.current?.share();
    },

    /** 写跟进 */
    handleFollowRecord() {
      detailRef.current?.followRecord();
    },

    /** 将详情权限数组转为事件映射，供顶部操作按钮判断 */
    getPermissionsMap(permissions: any[]) {
      return (Array.isArray(permissions) ? permissions : []).reduce((prev, cur) => ({ ...prev, [cur.event]: cur }), {});
    },
  });

  useEffect(() => {
    templateSessionRef.current = undefined;
    setTemplateSession(undefined);
    setChanceDrawerData(undefined);
    setCreatePermission(undefined);
    setLoadedScope(undefined);
    setDetail({});
    setIsUpdateMode(false);
    setLoadingSk(true);
    if (detailData.open && detailData.id) {
      methods.getDetail();
      if (isYiMing) methods.loadCreatePermission();
    }
    return () => { if (sourceScopeRef.current === sourceScope) sourceScopeRef.current = undefined; };
  }, [sourceScope]);


  const permissionsMap = methods.getPermissionsMap(detail?.permissions);

  // 从公海列表打开的详情为只读，隐藏顶部操作按钮（编辑/移交/共享/写跟进）
  const hideOperate = !!detailData?.fromPublicSea;

  const operateList = useMemo(() => {
    if (isUpdateMode) return [];
    return refactorPermissions([
      { event: 'createChance', name: `新建${chancepointAlias}`, visible: canCreateChance },
      { event: 'update', name: '编辑', type: 'primary', visible: true },
      { event: 'transfer', name: '移交', visible: !!permissionsMap['standardFranchisee:transfer'] },
      { event: 'share', name: '共享', visible: !!permissionsMap['standardFranchisee:share'] },
      { event: 'followRecord', name: '写跟进', visible: !!permissionsMap['standardFranchisee:followRecord'] },
    ].filter(itm => itm.visible)).map(itm => ({ type: 'default', ...itm }));
  }, [isUpdateMode, permissionsMap, canCreateChance, chancepointAlias]);

  return (
    <V2Drawer
      bodyStyle={{
        padding: 0,
      }}
      open={detailData.open}
      destroyOnClose
      onClose={methods.onCloseDrawer}
    >
      {
        loadingSk
          ? <SkeletonDetail/>
          : <V2Container
            // 容器上下padding 0， 所以减去就是0
            style={{ height: '100vh' }}
            emitMainHeight={h => setMainHeight(h)}
            className={styles.container}
            extraContent={{
              top: (
                <V2Title
                  text={`${franchiseeAlias}详情`}
                  className={styles.containerTop}
                  extra={
                    hideOperate ? null : <V2Operate operateList={operateList}
                      showBtnCount={5}
                      onClick={(btn) => methods[btn.func]()} />
                  }
                />
              ),
              bottom: isUpdateMode ? <div className={styles.containerBottom}>
                <Button type='primary' loading={submitting} onClick={methods.confirmHandle} className='ml-12'>
                  确定
                </Button>
                <Button onClick={methods.cancelHandle}>取消</Button>
              </div> : <></>
            }}
          >
            <div>
              <Spin spinning={loading}>
                <FranchiseeDetail
                  ref={detailRef}
                  mainHeight={mainHeight}
                  detail={detail}
                  isUpdateMode={isUpdateMode}
                  setIsUpdateMode={setIsUpdateMode}
                  style={{
                    padding: '0 40px 24px',
                  }}
                  setSubmitting={setSubmitting}
                  refresh={methods.refresh}
                  franchiseeAlias={franchiseeAlias}
                  expansionTaskAlias={expansionTaskAlias}
                  closeDrawer={methods.onCloseDrawer}
                />
              </Spin>
            </div>
          </V2Container>
      }

      {templateSession && templateSession.scope === sourceScope && canCreateChance && <SelectTemplateModal
        visible={true}
        setVisible={(visible) => methods.setTemplateVisible(visible, templateSession)}
        chancepointAlias={chancepointAlias}
        onSelectTemplate={(data) => methods.selectChanceTemplate(data, templateSession)}
      />}
      {chanceDrawerData?.open && chanceDrawerData.scope === sourceScope && canCreateChance && <ChanceCreateDrawer
        drawerData={{ open: true, templateId: chanceDrawerData.templateId, id: '' }}
        franchiseeSource={chanceDrawerData.source}
        chancepointAlias={chancepointAlias}
        closeHandle={methods.closeChanceDrawer}
        handleEditSuccessed={methods.closeChanceDrawer}
        handleCreateSuccessed={methods.closeChanceDrawer}
      />}
    </V2Drawer>
  );
};

export default DetailDrawer;
